import { orgQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import { useAppStore } from '@/stores/app';
import { Role, UserPublic } from '@/types';
import { isUnauthorizedError } from '@/types/errors';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_SEARCH_LIMIT, SEARCH_CACHE_TIME, TRANSLATION_KEYS, USER_SEARCH_MIN_LENGTH } from './constants';
import type { AddUserParams, OrgUser, UserSearchResult } from './types';
import { isValidUserSearchResults } from './validation';

/**
 * Hook to fetch organization users.
 *
 * Normalizes the API response by extracting `allowed_roles[orgId]` into a
 * flat `roles` array on each user, which is the org-scoped role list consumed
 * by the ManageUsers UI.
 */
export function useOrgUsers(orgId: string) {
  return useQuery<OrgUser[]>({
    queryKey: orgQueryKeys.users(orgId),
    queryFn: async () => {
      const response = await apiClient.get<UserPublic[]>(`/orgs/${orgId}/users`);
      return (response ?? []).map(user => ({
        ...user,
        roles: user.allowed_roles?.[orgId] ?? [],
      }));
    },
    retry: (failureCount, error) => {
      // Don't retry on unauthorized errors
      if (isUnauthorizedError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 0,
  });
}

/**
 * Hook to search for users with runtime validation
 * Returns error state directly for consumer to handle
 */
export function useUserSearch(searchQuery: string, t: TFunction) {
  return useQuery<UserSearchResult[]>({
    queryKey: orgQueryKeys.userSearch(searchQuery),
    queryFn: async () => {
      const orgId = useAppStore.getState().selectedOrganization?.id;
      if (!searchQuery || searchQuery.length < USER_SEARCH_MIN_LENGTH) {
        return [];
      }

      const response = await apiClient.post(`/orgs/${orgId}/users/search`, {
        q: searchQuery,
        limit: DEFAULT_SEARCH_LIMIT,
      });

      // Validate response structure at runtime
      if (!isValidUserSearchResults(response)) {
        const errorMsg = t(TRANSLATION_KEYS.USER_SEARCH_INVALID_RESPONSE);

        // Build safe debug info without exposing sensitive data
        const debugInfo = {
          responseType: typeof response,
          isArray: Array.isArray(response),
          length: Array.isArray(response) ? response.length : undefined,
          hasItems: Array.isArray(response) && response.length > 0,
          firstItemKeys: Array.isArray(response) && response.length > 0 ? Object.keys(response[0] || {}) : undefined,
        };

        console.error('Invalid response format from user search API', debugInfo);

        // Only include structural information in development to avoid exposing sensitive data
        const isDevelopment = import.meta.env.DEV;
        const detailedError = isDevelopment
          ? ` - Expected array of UserPublic objects with id, email, full_name. Received ${debugInfo.responseType}${debugInfo.isArray ? ` array with ${debugInfo.length} items` : ''}${debugInfo.firstItemKeys ? ` with keys: ${debugInfo.firstItemKeys.join(', ')}` : ''}.`
          : ' - Expected array of UserPublic objects with id, email, full_name.';

        throw new Error(`${errorMsg}${detailedError}`);
      }

      return response;
    },
    enabled: searchQuery.length >= USER_SEARCH_MIN_LENGTH,
    staleTime: SEARCH_CACHE_TIME,
    retry: 1,
  });
}

/**
 * Hook to add a user to an organization
 */
export function useAddUser(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: AddUserParams) => {
      const url = `/orgs/${orgId}/users`;
      const body = { email, roles };

      return await apiClient.post(url, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.users(orgId) });
    },
  });
}

/**
 * Hook to remove a user from an organization
 */
export interface RemoveUserParams {
  email: string;
  roles?: Role[] | null;
}

export function useRemoveUser(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: RemoveUserParams) => {
      return await apiClient.delete(`/orgs/${orgId}/users`, { email, roles });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.users(orgId) });
    },
  });
}

/**
 * Hook that provides the remove user handler with toast notifications
 */
export function useRemoveUserHandler(orgId: string, orgName: string, t: TFunction) {
  const removeUserMutation = useRemoveUser(orgId);

  return useCallback(
    ({ email, userName, roles }: { email: string; userName: string; roles: Role[] }) => {
      const removePromise = removeUserMutation.mutateAsync({ email, roles });

      toast.promise(removePromise, {
        loading: t(TRANSLATION_KEYS.REMOVING),
        success: () => t(TRANSLATION_KEYS.USER_REMOVED_SUCCESS, { name: userName, orgName }),
        error: (error: Error) => error.message || t(TRANSLATION_KEYS.USER_REMOVED_FAILED, { orgName }),
      });
    },
    [removeUserMutation, t, orgName],
  );
}

/**
 * Hook to update a user's roles in an organization
 */
export function useUpdateUserRoles(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: { email: string; roles: Role[] }) => {
      return await apiClient.post(`/orgs/${orgId}/users`, { email, roles });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.users(orgId) });
    },
  });
}
